import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const MAPS_URL =
    "https://www.google.com/maps/place/Psicologo+dott.+Giovanni+De+Lorenzo/@41.0062865,15.6249566,17z/data=!4m16!1m9!3m8!1s0x1339af00e6bd15f9:0xa83c5ae43b052758!2sPsicologo+dott.+Giovanni+De+Lorenzo!8m2!3d41.0062865!4d15.6275315!9m1!1b1!16s%2Fg%2F11rn7p5fmt!3m5!1s0x1339af00e6bd15f9:0xa83c5ae43b052758!8m2!3d41.0062865!4d15.6275315!16s%2Fg%2F11rn7p5fmt?entry=ttu&g_ep=EgoyMDI2MDkyMC4wIKXMDSoASAFQAw%3D%3D";

const INFO = [
    { Icon: Phone, label: "Telefono", value: "+39 334 176 8041", href: "tel:+393341768041", testid: "contact-phone" },
    { Icon: Mail, label: "Email", value: "studio@psicologogiovannidelorenzo.it", href: "mailto:studio@psicologogiovannidelorenzo.it", testid: "contact-email" },
    { Icon: MapPin, label: "Studio", value: "Via Alessandro Volta, 85025 Melfi (PZ)", href: MAPS_URL, testid: "contact-address" },
    { Icon: Clock, label: "Orari", value: "Lun – Sab: 08:00 – 21:00 · Domenica: chiuso", href: null, testid: "contact-hours" },
];

const initialForm = { nome: "", email: "", telefono: "", messaggio: "" };

export const Contatti = () => {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);

    const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        const data = new FormData(e.target);
        data.append("form-name", "richiesta-colloquio");
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams(data).toString(),
            });
            if (!res.ok) throw new Error("send failed");
            toast.success("Messaggio inviato con successo. Ti risponderò al più presto.");
            setForm(initialForm);
        } catch {
            toast.error("Non è stato possibile inviare il messaggio. Riprova tra poco.");
        } finally {
            setSending(false);
        }
    };

    const inputClass =
        "w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-3 text-base text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage transition-all duration-300";

    return (
        <section id="contatti" className="bg-cream" data-testid="contatti-section">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 md:py-32 grid lg:grid-cols-[1fr_1.15fr] gap-14 lg:gap-20">
                <div>
                    <Reveal>
                        <Eyebrow>Contatti</Eyebrow>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug text-ink" data-testid="contatti-title">
                            Fai il primo passo
                        </h2>
                        <p className="mt-6 text-base sm:text-lg text-[#526063] leading-relaxed">
                            Puoi scrivermi per richiedere informazioni o per prenotare un primo colloquio. Ogni messaggio viene letto con attenzione e trattato con riservatezza.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <ul className="mt-10 space-y-6">
                            {INFO.map((item) => (
                                <li key={item.label} className="flex items-start gap-4" data-testid={item.testid}>
                                    <span className="rounded-full bg-sage-light p-3 text-sage-dark shrink-0">
                                        <item.Icon size={18} strokeWidth={1.8} />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-ink">{item.label}</p>
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                target={item.href.startsWith("http") ? "_blank" : undefined}
                                                rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                                className="text-sm text-petrol hover:text-petrol-soft transition-colors duration-300 mt-0.5 inline-block"
                                            >
                                                {item.value}
                                            </a>
                                        ) : (
                                            <p className="text-sm text-[#7A8C8E] italic mt-0.5">{item.value}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 text-sm text-[#526063]" data-testid="contact-online-note">
                            Ricevo in studio a Melfi e <strong>online in videochiamata</strong>: puoi scegliere la modalità più comoda per te.
                        </p>
                        <div className="mt-8 overflow-hidden rounded-3xl border border-ink/10 shadow-sm" data-testid="contact-map">
                            <iframe
                                title="Mappa dello studio: Via Alessandro Volta, 85025 Melfi (PZ)"
                                src="https://maps.google.com/maps?q=41.0062865,15.6275315&z=17&output=embed"
                                className="w-full h-64 sm:h-72 border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <form
                        name="richiesta-colloquio"
                        method="POST"
                        data-netlify="true"
                        netlify-honeypot="bot-field"
                        onSubmit={submit}
                        className="rounded-3xl bg-white p-8 sm:p-10 shadow-xl shadow-petrol/5 border border-ink/5"
                        data-testid="contact-form"
                    >
                        <input type="hidden" name="form-name" value="richiesta-colloquio" />
                        <p className="hidden" aria-hidden="true">
                            <label>Non compilare questo campo: <input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
                        </p>
                        <h3 className="font-serif text-2xl font-semibold text-ink">Richiedi un primo colloquio</h3>
                        <p className="mt-2 text-sm text-[#526063]">
                            Compila il modulo: sarai ricontattato per concordare insieme il primo incontro.
                        </p>

                        <div className="mt-8 grid sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="nome" className="block text-sm font-semibold text-ink mb-2">Nome e Cognome *</label>
                                <input
                                    id="nome" name="nome" type="text" required minLength={2}
                                    value={form.nome} onChange={update}
                                    placeholder="Inserisci il tuo nome"
                                    className={inputClass}
                                    data-testid="contact-input-name"
                                />
                            </div>
                            <div>
                                <label htmlFor="telefono" className="block text-sm font-semibold text-ink mb-2">Telefono</label>
                                <input
                                    id="telefono" name="telefono" type="tel"
                                    value={form.telefono} onChange={update}
                                    placeholder="+39 333 1234567"
                                    className={inputClass}
                                    data-testid="contact-input-phone"
                                />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label htmlFor="email" className="block text-sm font-semibold text-ink mb-2">Email *</label>
                            <input
                                id="email" name="email" type="email" required
                                value={form.email} onChange={update}
                                placeholder="nome@esempio.it"
                                className={inputClass}
                                data-testid="contact-input-email"
                            />
                        </div>
                        <div className="mt-5">
                            <label htmlFor="messaggio" className="block text-sm font-semibold text-ink mb-2">Come posso aiutarti?</label>
                            <textarea
                                id="messaggio" name="messaggio" rows={4}
                                value={form.messaggio} onChange={update}
                                placeholder="Descrivi brevemente il motivo del contatto (opzionale)..."
                                className={`${inputClass} resize-none`}
                                data-testid="contact-input-message"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit-btn"
                            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-petrol text-cream px-8 py-4 text-base font-semibold hover:bg-petrol-soft hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            {sending ? "Invio in corso..." : "Invia richiesta"}
                        </button>
                        <p className="mt-4 text-xs text-[#7A8C8E] leading-relaxed text-center">
                            Inviando il modulo accetti la Privacy Policy. I tuoi dati saranno utilizzati esclusivamente per rispondere alla tua richiesta.
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
